/**
 * React Query hooks for submitted actions.
 * React Query hooks for the corresponding entity.
 */

import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { rememberSubmittedActionStatus } from "./submittedActionStatusCache";
import { queryKeys } from "common/queryKeys";
import { CubeQueryResponse, pollInsightsRefresh } from "./useInsightsQueries";

export type SubmittedActionEntry = {
  guid: string;
  deadline: string;
  categoryId: number;
  category: string;
  actionId: number;
  action: string;
  requiredEvidence: string;
  programmeId: string;
  date: string;
  email: string;
  detailQuestion: string;
  submittedDetails: string;
  status: string;
};

/**
 * API for fetching submitted actions
 */
async function fetchSubmittedActionsByEmail(
  email: string
): Promise<SubmittedActionEntry[]> {
  const res = await fetch(
    `${
      import.meta.env.VITE_API_ENDPOINT
    }/v1/submittedActions?email=${encodeURIComponent(email)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    }
  );
  // Return empty array if API returns error (e.g., 404, 500)
  if (!res.ok) {
    console.warn(`Failed to fetch submitted actions: ${res.status}`);
    return [];
  }
  return (await res.json()) as SubmittedActionEntry[];
}

/**
 * Hook to fetch submitted actions by email
 */
export function useSubmittedActions(email: string | undefined) {
  return useQuery({
    queryKey: queryKeys.programmeActions.submitted(email ?? ""),
    queryFn: async () => {
      if (!email) return [];
      return await fetchSubmittedActionsByEmail(email);
    },
    enabled: !!email,
  });
}

/**
 * Fetch submitted actions by email (used during initialization)
 */
export async function fetchSubmittedActionsQuery(
  email: string
): Promise<SubmittedActionEntry[]> {
  return await fetchSubmittedActionsByEmail(email);
}

/**
 * A single action to persist server-side via POST /v1/submittedActions.
 */
export type SubmittedActionInput = {
  categoryId: number;
  category: string;
  actionId: string;
  action: string;
  detailQuestion?: string;
  submittedDetails?: string;
};

async function postSubmittedActions(
  email: string,
  actions: SubmittedActionInput[]
): Promise<void> {
  const res = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/v1/submittedActions`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      // programmeGuid omitted → persisted as a self-selected (non-programme) action.
      body: JSON.stringify({ email, actions }),
    }
  );
  if (!res.ok) {
    throw new Error(`Failed to submit actions: ${res.status}`);
  }
}

/**
 * Hook to persist self-selected actions to the backend. Invalidates the
 * submitted-actions query on success so the new actions surface in the planner.
 */
export function useSubmitActions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      email,
      actions,
    }: {
      email: string;
      actions: SubmittedActionInput[];
    }) => postSubmittedActions(email, actions),
    onSuccess: (_data, { email }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.programmeActions.submitted(email),
      });
    },
  });
}

async function deleteSubmittedAction(guid: string): Promise<void> {
  const res = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/v1/submittedActions`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ guid }),
    }
  );
  if (!res.ok) {
    throw new Error(`Failed to delete action: ${res.status}`);
  }
}

/**
 * Hook to remove one of the farmer's own self-selected actions by its
 * submission guid. Invalidates the submitted-actions query on success.
 */
export function useDeleteAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ guid }: { guid: string; email: string }) =>
      deleteSubmittedAction(guid),
    onSuccess: (_data, { email }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.programmeActions.submitted(email),
      });
    },
  });
}

// Statuses a corporate user is allowed to set via PATCH /v1/submittedActions/status.
export type CorporateSettableStatus = "submitted" | "on-track" | "completed";

async function patchSubmittedActionStatus(
  guid: string,
  status: CorporateSettableStatus
): Promise<void> {
  const res = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/v1/submittedActions/status`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ guid, status }),
    }
  );
  if (!res.ok) {
    throw new Error(`Failed to update action status: ${res.status}`);
  }
}

/**
 * Hook for a corporate user to set an action to submitted, on-track,
 * or completed. Refreshes every insights view derived from the
 * SubmittedActions cube (status affects the by-farmer and by-action tables,
 * category summaries, and the action detail dialog itself).
 */
export function useUpdateSubmittedActionStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      guid,
      status,
    }: {
      guid: string;
      status: CorporateSettableStatus;
    }) => patchSubmittedActionStatus(guid, status),
    onSuccess: async (_, { guid, status }) => {
      const detailQueries = {
        predicate: (query: { queryKey: readonly unknown[] }) =>
          query.queryKey[0] === "insights" &&
          typeof query.queryKey[1] === "string" &&
          (query.queryKey[1].startsWith("farmerActions_") ||
            query.queryKey[1].startsWith("farmersByAction_")),
      };
      // Prevent an in-flight pre-save response from replacing the saved status.
      await queryClient.cancelQueries(detailQueries);
      rememberSubmittedActionStatus(queryClient, guid, status);
      queryClient.setQueriesData<CubeQueryResponse<Record<string, unknown>>>(
        detailQueries,
        (previous) =>
          previous && {
            ...previous,
            data: previous.data.map((row) =>
              row["SubmittedActions.guid"] === guid
                ? { ...row, "SubmittedActions.status": status }
                : row
            ),
          }
      );
      pollInsightsRefresh(queryClient, [""]);
    },
  });
}

/**
 * Reset submitted actions query cache
 */
export function resetSubmittedActionsQueryCache(
  queryClient: QueryClient
): void {
  queryClient.removeQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      query.queryKey[0] === "programmeActions" &&
      query.queryKey[1] === "submitted",
  });
}
